import { uppercaseFirstCharacter } from "@sg-protect/components";
import { useLogger } from "@sg-protect/federation-runtime/squide";
import config from "@sg-protect/runtime-config";
import type { MixpanelTrackingFunctionOptions } from "@workleap/telemetry/react";
import { useMixpanelProviderProperties, useMixpanelTrackingFunction } from "@workleap/telemetry/react";
import { useCallback } from "react";
import { MixpanelEventName } from "./mixpanelEventName.ts";
import { MixpanelProperty } from "./mixpanelProperty.ts";
import { useRouteTrackingName } from "./useRouteTrackingName.ts";

export type BulkActionInitiatorSource = "Web" | "Automation";

export type TrackEventHandler = (
    eventName: MixpanelEventName,
    properties: TrackEventProperties,
    options?: MixpanelTrackingFunctionOptions
) => Promise<void>;

export type TrackEventProperties = Partial<Record<MixpanelProperty, unknown>>;

export const useTrackEvent = () => {
    const pageName = useRouteTrackingName();
    const track = useMixpanelTrackingFunction();

    return useCallback(
        async (
            eventName: MixpanelEventName,
            properties: TrackEventProperties,
            options?: MixpanelTrackingFunctionOptions
        ) => {
            const allProperties = {
                [MixpanelProperty.Name]: pageName || "",
                [MixpanelProperty.Region]: config.region, // TODO: This should be moved to a MixpanelPropertiesProvider in the host app at some point when we handle the region in the host app
                ...properties
            };

            await track(eventName, allProperties, options);
        },
        [track, pageName]
    );
};

export function useTrackButtonClicked() {
    const track = useTrackEvent();

    return useCallback(
        (trigger: string, location: string, extraProperties = {}, options?: MixpanelTrackingFunctionOptions) => {
            void track(
                MixpanelEventName.ButtonClicked,
                {
                    [MixpanelProperty.Trigger]: trigger,
                    [MixpanelProperty.Location]: location,
                    ...extraProperties
                },
                options
            );
        },
        [track]
    );
}

export function useTrackTabChanged() {
    const track = useTrackEvent();

    return useCallback(
        (trigger: string, location: string, extraProperties = {}, options?: MixpanelTrackingFunctionOptions) => {
            void track(
                MixpanelEventName.TabChanged,
                {
                    [MixpanelProperty.Trigger]: trigger,
                    [MixpanelProperty.Location]: location,
                    ...extraProperties
                },
                options
            );
        },
        [track]
    );
}

export function useTrackLinkClicked() {
    const track = useTrackEvent();

    return useCallback(
        (trigger: string, location: string, extraProperties = {}, options?: MixpanelTrackingFunctionOptions) => {
            void track(
                MixpanelEventName.LinkClicked,
                {
                    [MixpanelProperty.Trigger]: trigger,
                    [MixpanelProperty.Location]: location,
                    ...extraProperties
                },
                options
            );
        },
        [track]
    );
}

export function useTrackRadioChanged() {
    const track = useTrackEvent();

    return useCallback(
        (
            field: string,
            value: string,
            location: string,
            extraProperties = {},
            options?: MixpanelTrackingFunctionOptions
        ) => {
            void track(
                MixpanelEventName.RadioChanged,
                {
                    [MixpanelProperty.Field]: field,
                    [MixpanelProperty.Values]: value,
                    [MixpanelProperty.Location]: location,
                    ...extraProperties
                },
                options
            );
        },
        [track]
    );
}

export function useTrackSwitchChanged() {
    const track = useTrackEvent();

    return useCallback(
        (field: string, isChecked: boolean, extraProperties = {}, options?: MixpanelTrackingFunctionOptions) => {
            void track(
                MixpanelEventName.SwitchChanged,
                {
                    [MixpanelProperty.Field]: field,
                    [MixpanelProperty.IsChecked]: isChecked,
                    ...extraProperties
                },
                options
            );
        },
        [track]
    );
}

function useTrackModalOpened() {
    const track = useTrackEvent();

    return useCallback(
        (modalName: string, trigger?: string, extraProperties = {}, options?: MixpanelTrackingFunctionOptions) => {
            void track(
                MixpanelEventName.ModalOpened,
                {
                    [MixpanelProperty.Name]: modalName,
                    [MixpanelProperty.Trigger]: trigger,
                    ...extraProperties
                },
                options
            );
        },
        [track]
    );
}

function useTrackModalClosed() {
    const track = useTrackEvent();

    return useCallback(
        (modalName: string, closeAction?: string, extraProperties = {}, options?: MixpanelTrackingFunctionOptions) => {
            void track(
                MixpanelEventName.ModalClosed,
                {
                    [MixpanelProperty.Name]: modalName,
                    [MixpanelProperty.CloseAction]: closeAction,
                    ...extraProperties
                },
                options
            );
        },
        [track]
    );
}

export function useTrackModalState() {
    const trackModalOpened = useTrackModalOpened();
    const trackModalClosed = useTrackModalClosed();

    return [trackModalOpened, trackModalClosed] as const;
}

export function useTrackFlowCompleted() {
    const track = useTrackEvent();

    return useCallback(
        (flowName: string, extraProperties = {}, options?: MixpanelTrackingFunctionOptions) => {
            void track(
                MixpanelEventName.FlowCompleted,
                { [MixpanelProperty.FlowName]: flowName, ...extraProperties },
                options
            );
        },
        [track]
    );
}

export function useTrackFlowFailed() {
    const track = useTrackEvent();

    return useCallback(
        (flowName: string, extraProperties = {}, options?: MixpanelTrackingFunctionOptions) => {
            void track(
                MixpanelEventName.FlowFailed,
                { [MixpanelProperty.FlowName]: flowName, ...extraProperties },
                options
            );
        },
        [track]
    );
}

export function useTrackFlowInitiated() {
    const track = useTrackEvent();

    return useCallback(
        (flowName: string, extraProperties = {}, options?: MixpanelTrackingFunctionOptions) => {
            void track(
                MixpanelEventName.FlowInitiated,
                { [MixpanelProperty.FlowName]: flowName, ...extraProperties },
                options
            );
        },
        [track]
    );
}

export function useTrackBulkActionTriggered() {
    const track = useTrackEvent();
    const logger = useLogger();
    const providerProperties = useMixpanelProviderProperties();

    return useCallback(
        (
            action: string,
            initiatorSource: BulkActionInitiatorSource,
            extraProperties: TrackEventProperties = {},
            options?: MixpanelTrackingFunctionOptions
        ) => {
            if (
                process.env.NODE_ENV !== "production" &&
                !providerProperties[MixpanelProperty.Location] &&
                !extraProperties[MixpanelProperty.Location]
            ) {
                logger.warning(
                    `[tracking] BulkActionTriggered has no Location. Did you forget to add a 'mixpanelLocation' property to the route's 'handle' object?`
                );
            }

            void track(
                MixpanelEventName.BulkActionTriggered,
                {
                    [MixpanelProperty.Action]: uppercaseFirstCharacter(action),
                    [MixpanelProperty.InitiatorSource]: initiatorSource,
                    ...extraProperties
                },
                options
            );
        },
        [track, logger, providerProperties]
    );
}
